import { CASE_STATE_TONE, type CaseState } from '@concord/domain';
import { Button, Dialog, Field, Notice, Panel, PanelHead, Pill, useToast } from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { type NavigatorOption, navigatorLabel } from '../case/ReassignCase';

export interface OwnedCase {
  id: string;
  reference: string | null;
  state: CaseState;
  stateLabel: string;
}

/** Active navigators other than `exceptId`, for handing cases over. */
export function useOtherNavigators(exceptId: string) {
  const q = useQuery({
    queryKey: ['navigators', 'directory'],
    queryFn: () => api<{ items: NavigatorOption[] }>('/staff/directory/navigators'),
  });
  return { ...q, others: (q.data?.items ?? []).filter((n) => n.id !== exceptId) };
}

/** The picker in the disable dialog: who takes this navigator's open cases. */
export function HandoverSelect({
  count,
  options,
  value,
  onChange,
}: {
  count: number;
  options: NavigatorOption[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <Field
      label={`Hand their ${count} open ${count === 1 ? 'case' : 'cases'} to`}
      hint="Each case is reassigned and logged first; the patients see the new navigator on their home page."
    >
      {(id, d) => (
        <select
          id={id}
          aria-describedby={d}
          className="select"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((n) => (
            <option key={n.id} value={n.id}>
              {navigatorLabel(n)}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

/** A navigator's open cases on their account record, with a one-step handover. */
export function OwnedCasesPanel({
  accountId,
  name,
  cases,
  defaultTo,
}: {
  accountId: string;
  name: string;
  cases: OwnedCase[];
  defaultTo: string;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const { others } = useOtherNavigators(accountId);
  const [to, setTo] = useState('');
  const choice = to || (others.some((n) => n.id === defaultTo) ? defaultTo : (others[0]?.id ?? ''));
  const handover = useMutation({
    mutationFn: () =>
      api<{ moved: number }>(`/staff/directory/account/${accountId}/reassign-cases`, {
        body: { toAccountId: choice },
      }),
    onSuccess: async (r) => {
      setOpen(false);
      await qc.invalidateQueries({ queryKey: ['account', accountId] });
      await qc.invalidateQueries({ queryKey: ['navigators'] });
      toast(`${r.moved} ${r.moved === 1 ? 'case' : 'cases'} handed over.`);
    },
  });
  return (
    <Panel aria-label="Open cases they own">
      <PanelHead title="Open cases they own" sub={cases.length}>
        {cases.length > 0 && others.length > 0 && (
          <Button variant="sec" onClick={() => setOpen(true)} style={{ marginLeft: 'auto' }}>
            Hand over open cases
          </Button>
        )}
      </PanelHead>
      <div className="pbody" style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
        {cases.length === 0 && <span className="sub2">No open cases.</span>}
        {cases.map((c) => (
          <button
            key={c.id}
            type="button"
            className="link"
            style={{ textAlign: 'left', display: 'flex', gap: '.5rem', alignItems: 'center' }}
            onClick={() => c.reference && navigate(`/console/cases/${c.reference}`)}
          >
            <span className="ref">{c.reference ?? 'Draft'}</span>{' '}
            <Pill tone={CASE_STATE_TONE[c.state]}>{c.stateLabel}</Pill>
          </button>
        ))}
      </div>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={`Hand over ${name}'s open cases`}
        description="Use this when someone is away. Each case is reassigned and logged; nothing else about the cases changes."
        footer={
          <>
            <Button variant="sec" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button busy={handover.isPending} disabled={!choice} onClick={() => handover.mutate()}>
              Hand over {cases.length}
            </Button>
          </>
        }
      >
        <HandoverSelect count={cases.length} options={others} value={choice} onChange={setTo} />
        {handover.isError && <Notice tone="bad">{errorMessage(handover.error)}</Notice>}
      </Dialog>
    </Panel>
  );
}
